/**
 * signedUrls.js · v9.7.0 · Helper para URLs firmadas de archivos
 * ═══════════════════════════════════════════════════════════════
 * Las etiquetas <img> no envían tokens Bearer. Este helper solicita
 * al backend URLs firmadas con HMAC (vida corta) para mostrar imágenes.
 *
 * Uso:
 *   import { getSignedUrls, signSingleUrl } from "../lib/signedUrls";
 *   const signed = await getSignedUrls([url1, url2]);
 *   <img src={signed[url1]} />
 */
import api from "./api";

const _cache = new Map(); // path → { signedUrl, expiresAt }
const CACHE_MARGIN = 120_000; // refrescar 2min antes de expirar

/**
 * Firma un batch de paths/URLs. Devuelve un mapa { originalUrl → signedUrl }.
 * Usa cache local para evitar llamadas redundantes.
 */
export async function getSignedUrls(paths) {
  if (!paths || !paths.length) return {};

  const now = Date.now();
  const result = {};
  const toSign = [];

  for (const p of paths) {
    if (!p) continue;
    const cached = _cache.get(p);
    if (cached && cached.expiresAt > now + CACHE_MARGIN) {
      result[p] = cached.signedUrl;
    } else {
      toSign.push(p);
    }
  }

  if (toSign.length > 0) {
    try {
      const { data } = await api.post("/v1/fiscalizacion/sign-urls", {
        paths: toSign,
        ttl: 900,
      });
      const signed = data.signed_urls || {};
      const ttlMs = (data.ttl || 900) * 1000;
      for (const [orig, signedUrl] of Object.entries(signed)) {
        _cache.set(orig, { signedUrl, expiresAt: now + ttlMs });
        result[orig] = signedUrl;
      }
    } catch (e) {
      console.error("signedUrls: Error firmando URLs", e);
      // Fallback: devolver paths originales (darán 401 pero al menos no rompe)
      for (const p of toSign) {
        result[p] = p;
      }
    }
  }

  return result;
}

/**
 * Firma una sola URL. Conveniencia sobre getSignedUrls.
 */
export async function signSingleUrl(path) {
  if (!path) return null;
  const map = await getSignedUrls([path]);
  return map[path] || path;
}

/**
 * Limpia el cache de URLs firmadas.
 */
export function clearSignedUrlCache() {
  _cache.clear();
}
